// tslint:disable:no-commented-code
// tslint:disable:no-duplicate-string
// tslint:disable:only-arrow-functions

import {
  preOrderLocale,
  recommendationsPanelsLocaleKeys,
  reOrderLocale,
} from '@epicuro-next/constants/features/localization';
import {
  IDynamicItem,
  IDynamicSimpleText,
} from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';

import { getIdProvider as getIdProviderUtil } from '../../../utils/dynamic-items-utils';
import {
  getScrollableContainer,
  ScrollableContainerDataProviders,
} from '../../components/scrollable-container';
import {
  errorMessage,
  hasError,
  isLoading,
} from '../../loading-state-wrapper-helpers';
import { conditionalMessage } from '../../misc';
import {
  getPanelTrackingIdProperty,
  PanelTrackingIdConstants,
} from '../../panel-tracking-id';
import { hasData, hasNoItems, productData } from '../../product-data';
import {
  getServingConfigProperty,
  servingconfigPropertyName,
} from '../../serving-config';
import { BestPerformingArticlesProviderConfigArgs } from '../../types/best-performing-articles-provider';
import {
  BrandOrgType,
  getBrandOrganisation,
} from '../common/bought-this-season/brand-org';

import { getDataForBestPerformingArticles } from './get-best-performing-articles-helper';

const ids = {
  itemArrayRenderer: 'items-array-renderer',
  itemMediaPreview: 'item-media-preview',
  loadingSkeletonsArrayRenderer: 'loading-skeletons-array-renderer',
  loadingSkeletonItem: 'loading-skeleton-item',
  scrollableContainer: 'scrollable-container',
  horizontalScrollablePanelContainer: 'scrollable-panel-container',
  horizontalScrollablePanelRoot: 'root',
};

const propertyNames = {
  bestPerformingArticlesProviderConfig: 'bestPerformingArticlesProviderConfig',
  brandOrganisation: 'brandOrganisation',
  brandOrganisationId: 'brandOrganisationId',
  hasData: 'hasData',
  hasError: 'hasError',
  hasNoItems: 'hasNoItems',
  isLoading: 'isLoading',
  loadingStateWrapper: 'loadingStateWrapper',
  panelName: 'panelName',
  panelStyle: 'panelStyle',
  panelStyleConstants: 'panelStyleConstants',
  panelStyleConstantsExt: 'panelStyleConstantsExt',
  preorderText: 'preorderText',
  productData: 'productData',
  reorderText: 'reorderText',
  servingConfig: servingconfigPropertyName,
};

function getIdProvider(
  idKey: keyof typeof ids,
  propertyNameKey: keyof typeof propertyNames,
) {
  const id = ids[idKey];
  const property = propertyNames[propertyNameKey];
  return getIdProviderUtil(id, property);
}

const defaultBestPerformingArticlesProviderArgs: BestPerformingArticlesProviderConfigArgs =
  {
    category: null,
    numberOfDays: 7,
    orderType: 'reorder',
    productsAmount: 10,
    sortBy: 'Popularity',
  };

const panelStyleConstants: PanelTrackingIdConstants = {
  page: 'md',
  location: '',
  type: 'hr',
  provider: 'nebp',
  cssVariant: '001',
};

const idProviders = {
  bestPerformingArticlesProvidersArgs: getIdProvider(
    'horizontalScrollablePanelRoot',
    'bestPerformingArticlesProviderConfig',
  ),
  brandOrganisation: getIdProvider(
    'horizontalScrollablePanelRoot',
    'brandOrganisation',
  ),
  brandOrganisationId: getIdProvider(
    'horizontalScrollablePanelRoot',
    'brandOrganisationId',
  ),
  panelName: getIdProvider('horizontalScrollablePanelRoot', 'panelName'),
  panelStyle: getIdProvider('horizontalScrollablePanelContainer', 'panelStyle'),
  servingConfig: getIdProvider(
    'horizontalScrollablePanelContainer',
    'servingConfig',
  ),
};

const headerText: IDynamicSimpleText = {
  id: 'header-text',
  contentTypeId: 'dynamic-elements-simple-text',
  styles: {
    classes: ['font-weight-bold'],
    style: {
      'font-size': '18px',
    },
    variables: {
      '--text-dynamic-color': 'var(--dark-mode-gray-600, #414141)',
    },
  },
  data: {
    [propertyNames.reorderText]: reOrderLocale,
    [propertyNames.preorderText]: preOrderLocale,
  },
  inputs: {
    text: {
      values: [
        idProviders.bestPerformingArticlesProvidersArgs,
        idProviders.brandOrganisation,
        { provider: 'self', property: propertyNames.reorderText },
        { provider: 'self', property: propertyNames.preorderText },
      ],
      transform: function ([
        bestPerformingArticlesProvidersArgs,
        brandOrg,
        reorderText,
        preorderText,
      ]: [
        BestPerformingArticlesProviderConfigArgs,
        BrandOrgType,
        string,
        string,
      ]): string {
        const orderTypeStr = (
          bestPerformingArticlesProvidersArgs.orderType === 'reorder'
            ? reorderText
            : preorderText
        ).toLowerCase();

        const str =
          window.neoclesDynamicFramework.recommendationsPanels.methods.text.getMostReorderedProductsHeading(
            bestPerformingArticlesProvidersArgs.numberOfDays,
            brandOrg.displayName,
            orderTypeStr,
          );

        return str;
      }.toString(),
    },
  },
};

const dataProviders: ScrollableContainerDataProviders = {
  isLoading: {
    provider: 'parent',
    property: propertyNames.isLoading,
  },
  hasError: {
    provider: 'parent',
    property: propertyNames.hasError,
  },
};

const scrollableContainer = getScrollableContainer(dataProviders);

const horizontalScrollablePanelContainer: IDynamicItem = {
  id: ids.horizontalScrollablePanelContainer,
  contentTypeId: 'dynamic-collections-renderer',
  data: {
    [propertyNames.loadingStateWrapper]: getDataForBestPerformingArticles(
      idProviders.panelName,
      idProviders.brandOrganisationId,
      idProviders.bestPerformingArticlesProvidersArgs,
    ),
    [propertyNames.isLoading]: isLoading(),
    [propertyNames.productData]: productData(),
    [propertyNames.hasError]: hasError(),
    [propertyNames.hasData]: hasData('self'),
    [propertyNames.hasNoItems]: hasNoItems(),
    [propertyNames.panelStyleConstants]: panelStyleConstants,
    [propertyNames.panelStyleConstantsExt]: {
      values: [
        { provider: 'self', property: propertyNames.panelStyleConstants },
        idProviders.bestPerformingArticlesProvidersArgs,
      ],
      transform: function ([
        constants,
        bestPerformingArticlesProviderConfigArgs,
      ]: [PanelTrackingIdConstants, BestPerformingArticlesProviderConfigArgs]) {
        const nrOfDays = bestPerformingArticlesProviderConfigArgs.numberOfDays
          .toString()
          .padStart(3, '0');
        constants.type = `${constants.type}${nrOfDays}`;
        return constants;
      }.toString(),
    },
    [propertyNames.servingConfig]: getServingConfigProperty(
      'self',
      propertyNames.loadingStateWrapper,
    ).servingConfig,
    ...getPanelTrackingIdProperty({
      provider: 'self',
      property: propertyNames.panelStyleConstantsExt,
    }),
  },
  styles: {
    classes: ['d-block', 'mt-2'],
  },
  children: [
    headerText,
    {
      contentTypeId: 'dynamic-collections-renderer',
      children: [
        scrollableContainer.scrollableContainer,
        errorMessage(),
        conditionalMessage(
          'no-items-message',
          'parent',
          'hasNoItems',
          recommendationsPanelsLocaleKeys.noRecommendedItemsReturned,
        ),
      ],
    },
  ],
};

export const horizontalScrollablePanelBestPerformingArticles: IDynamicItem = {
  id: ids.horizontalScrollablePanelRoot,
  contentTypeId: 'dynamic-collections-renderer',
  data: {
    [propertyNames.bestPerformingArticlesProviderConfig]:
      defaultBestPerformingArticlesProviderArgs,
    [propertyNames.brandOrganisation]: getBrandOrganisation(),
    [propertyNames.brandOrganisationId]: {
      provider: 'self',
      property: propertyNames.brandOrganisation,
      transform: function (brandOrg: BrandOrgType): string {
        return brandOrg.brandOrganisationId;
      }.toString(),
    },
    [propertyNames.panelName]: 'LandingPage',
  },
  children: [horizontalScrollablePanelContainer],
};
